import { ChangeEvent, useContext, useEffect, useState } from 'react';

import {
  Box,
  HStack,
  InputProps,
  Stack,
  StyleProps,
  SystemStyleObject,
  Text,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { getIn, useFormikContext } from 'formik';
import { ValidationError } from 'yup';

import { useUpdateSsnMutation } from '../../gql/generated/graphql';

import Input from '../shared/Input';

import MaskedSsnButton from './MaskedSsnButton';
import { SsnMask } from './MaskedSsnCustomerInfo';
import { SsnType, getSsnDetails } from './utils';

import colors from '../../chakra/foundations/colors';
import { DealContext } from '../../libs/DealContext';
import {
  isSsnAnItin,
  ssnRegExpFull,
  ssnRegExpLast4Min,
  ssnRegExpLast6Min,
  validateSsn,
} from '../../utils/validation/ssn';

type MaskedSsnInputProps = {
  ssnIdField: string;
  ssnValidField: string;
  hasItinField?: string;
  ssnId: number | null;
  ssnType?: SsnType;
  isDisabled?: boolean;
  replaceFixedCharacters?: boolean;
  width?: StyleProps['w'];
  inputProps?: InputProps;
  optional?: boolean;
};

const MaskedSsnInput = ({
  ssnIdField,
  ssnValidField,
  hasItinField,
  ssnId,
  ssnType,
  isDisabled = false,
  replaceFixedCharacters = false,
  width = '100%',
  inputProps,
  optional = false,
}: MaskedSsnInputProps) => {
  const ssnDetails = getSsnDetails(ssnType);

  const { deal, setUserChangedSomething, setShouldRecompareSocials } = useContext(DealContext);
  const { values, setFieldValue, validateForm } = useFormikContext();
  const [saveSSn] = useUpdateSsnMutation();

  const isCobuyer = ssnIdField.startsWith('cobuyer');
  const isLessee = ssnIdField.startsWith('original_lessee');

  const [ssn, setSsn] = useState<string | null>(null);
  const [savedSsnId, setSavedSsnId] = useState<number | null | undefined>(ssnId);
  const [isSsnHidden, setIsSsnHidden] = useState<boolean>(!!ssnId);

  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const maskedStyles = useMultiStyleConfig('Input') as {
    field?: { _disabled?: SystemStyleObject };
  };

  useEffect(() => {
    if (!ssn) {
      setSsn(ssnDetails.emptyMask);
    }

    if (replaceFixedCharacters) {
      const fixedCharactersRegExp = new RegExp(/x/gi);
      const maskPlaceholder = '_';

      if (ssn?.match(fixedCharactersRegExp)) {
        setSsn(ssn.replace(fixedCharactersRegExp, maskPlaceholder));
      }
    }
  }, [ssn]);

  const syncSsnValidityFields = (value: string) => {
    if (!/has_(full|last_4|last_6)_ssn$/.test(ssnValidField)) {
      return;
    }

    const baseField = ssnValidField.replace(/has_(full|last_4|last_6)_ssn$/, 'has_');
    const hasFullSsn = ssnRegExpFull.test(value);
    const hasLast6Ssn = hasFullSsn || ssnRegExpLast6Min.test(value);
    const hasField = (path: string) => getIn(values, path) !== undefined;

    if (ssnValidField.endsWith('has_last_4_ssn')) {
      const hasLast4Ssn = hasLast6Ssn || ssnRegExpLast4Min.test(value);
      if (hasField(`${baseField}last_4_ssn`)) {
        setFieldValue(`${baseField}last_4_ssn`, hasLast4Ssn, true);
      }
      if (hasField(`${baseField}full_ssn`)) {
        setFieldValue(`${baseField}full_ssn`, hasFullSsn, true);
      }
      return;
    }

    if (ssnValidField.endsWith('has_last_6_ssn')) {
      if (hasField(`${baseField}last_6_ssn`)) {
        setFieldValue(`${baseField}last_6_ssn`, hasLast6Ssn, true);
      }
      if (hasField(`${baseField}full_ssn`)) {
        setFieldValue(`${baseField}full_ssn`, hasFullSsn, true);
      }
      return;
    }

    if (hasField(`${baseField}full_ssn`)) {
      setFieldValue(`${baseField}full_ssn`, hasFullSsn, true);
    }
  };

  const handleSsnView = async (hidden: boolean, newSsn?: string, newSsnId?: number | null) => {
    if (newSsn) {
      setSsn(newSsn);
    }
    if (newSsnId) {
      setSavedSsnId(newSsnId);
    }
    setIsSsnHidden(hidden);
    setError(undefined);

    if (newSsn) {
      try {
        await validateSsn(isCobuyer, ssnDetails.regExp, optional).validate(newSsn);
        syncSsnValidityFields(newSsn);
      } catch (validationError) {
        syncSsnValidityFields(newSsn);
      }
    }
  };

  const handleSsnChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.match(/[a-wyz]/g)) {
      return;
    }
    const updatedValue = e.target.value.replace(/x/g, 'X');
    setSsn(updatedValue);

    try {
      await validateSsn(isCobuyer, ssnDetails.regExp, optional).validate(updatedValue);
      if (hasItinField) {
        setFieldValue(hasItinField, isSsnAnItin(updatedValue), false);
      }
      setError(undefined);
      syncSsnValidityFields(updatedValue);
    } catch (validationError) {
      syncSsnValidityFields(updatedValue);
    }
  };

  const handleBlur = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.match(/[a-wyz]/g)) {
      return;
    }
    const updatedValue = e.target.value.replace(/x/g, 'X');
    setIsLoading(true);
    setSsn(updatedValue);

    try {
      await validateSsn(isCobuyer, ssnDetails.regExp, optional).validate(updatedValue);
      setError(undefined);
      const result = await saveSSn({
        variables: {
          ssnId: savedSsnId,
          ssn: updatedValue,
        },
      });

      setSavedSsnId(result.data?.updateSSN ?? null);

      setFieldValue(ssnIdField, result.data?.updateSSN ?? null, true);
      syncSsnValidityFields(updatedValue);
      if (hasItinField) {
        setFieldValue(hasItinField, isSsnAnItin(updatedValue), false);
      }
      setUserChangedSomething(true);
      setShouldRecompareSocials(true);
      setTimeout(() => {
        validateForm();
      }, 0);
    } catch (validationError) {
      syncSsnValidityFields(updatedValue);
      setError((validationError as ValidationError).message);
    } finally {
      setIsLoading(false);
      if (updatedValue === '') {
        setFieldValue(ssnIdField, null, true);
      }
    }
  };

  return isSsnHidden ? (
    <Stack width={width}>
      <Text fontSize="sm">{ssnDetails.label}</Text>
      <Box
        borderWidth="1px"
        borderColor="darkLiver"
        borderRadius="2px"
        h="32px"
        minWidth="130px"
        // eslint-disable-next-line no-underscore-dangle
        sx={isDisabled ? maskedStyles.field?._disabled : undefined}
        bgColor={colors.white}
      >
        <HStack spacing="auto" width="100%" pt="2px" pl={2}>
          <SsnMask type="form" />
          <MaskedSsnButton
            type="form"
            onClick={(_, newSsn, newSsnId) => handleSsnView(false, newSsn ?? '', newSsnId ?? null)}
            {...(isLessee
              ? { originalLesseeId: deal?.original_lessee?.id }
              : { customerId: isCobuyer ? deal?.cobuyer?.id : deal?.customer?.id })}
          />
        </HStack>
      </Box>
    </Stack>
  ) : (
    <Input
      label={ssnDetails.label}
      name={ssnIdField}
      mask={ssnDetails.mask}
      value={ssn ?? ''}
      onChange={handleSsnChange}
      onBlur={handleBlur}
      isDisabled={isDisabled}
      isLoading={isLoading}
      customError={error}
      isInvalid={!!error}
      viewButton={!!ssnId}
      turnViewOff={ssnId ? () => handleSsnView(true) : undefined}
      formControlProps={{
        width,
      }}
      inputProps={inputProps}
    />
  );
};

export default MaskedSsnInput;
